import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetProfiles, apiGetDiary } from "@/lib/api";
import { Plus, MessageCircle, BookOpen, LogOut, Heart, Clock, ArrowRight, Sparkles, Settings, Pencil } from "lucide-react";
import { motion } from "framer-motion";

export default function DashboardPage() {
  const { user, token, logout } = useAuth();
  const nav = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [diaryCount, setDiaryCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      apiGetProfiles(token).catch(() => []),
      apiGetDiary(token).catch(() => []),
    ]).then(([p, d]) => {
      setProfiles(p);
      setDiaryCount(d.length);
    }).finally(() => setLoading(false));
  }, [token]);

  const handleLogout = () => { logout(); nav("/"); };

  const totalChats = profiles.reduce((acc, p) => acc + (p.status === "ready" ? 1 : 0), 0);

  return (
    <div className="min-h-screen bg-gradient-hero" data-testid="dashboard-page">
      {/* Top bar */}
      <header className="border-b border-border/40 bg-card/60 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1" data-testid="dashboard-logo">
            <span className="font-serif text-xl font-bold text-foreground">Io</span>
            <span className="font-serif text-xl font-bold text-accent">Ci</span>
            <span className="font-serif text-xl font-bold text-foreground">Sono</span>
            <span className="text-[10px] text-muted-foreground font-sans ml-0.5">.org</span>
          </Link>
          <nav className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/diary")} className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-diary">
              <BookOpen className="w-4 h-4 mr-1.5" /> Diario
            </Button>
            <Button variant="ghost" size="icon" onClick={() => nav("/settings")} className="h-8 w-8 text-muted-foreground" data-testid="nav-settings">
              <Settings className="w-4 h-4" />
            </Button>
            <div className="w-px h-5 bg-border/50 mx-1" />
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="h-8 w-8 text-muted-foreground" data-testid="dashboard-logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </nav>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8">
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
            Bentornato, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Il tuo spazio per mantenere vivi i ricordi.</p>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
          className="grid grid-cols-3 gap-3 mb-8">
          <Card className="bg-card border-border/30 shadow-soft">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground" data-testid="stat-profiles">{profiles.length}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Profili creati</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/30 shadow-soft">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground" data-testid="stat-chats">{totalChats}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Chat attive</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/30 shadow-soft">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground" data-testid="stat-diary">{diaryCount}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Voci diario</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
          className="grid sm:grid-cols-2 gap-3 mb-10">
          <Card className="bg-card border-border/30 shadow-soft hover:shadow-medium transition-all duration-300 cursor-pointer group"
            onClick={() => nav("/profile/create")} data-testid="action-new-profile">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/15 transition-colors">
                <Plus className="w-5 h-5 text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-sm font-bold text-foreground">Nuovo profilo</h3>
                <p className="text-[11px] text-muted-foreground">Rivivi un rapporto con chi non c'e piu</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
            </CardContent>
          </Card>
          <Card className="bg-card border-border/30 shadow-soft hover:shadow-medium transition-all duration-300 cursor-pointer group"
            onClick={() => nav("/diary")} data-testid="action-diary">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center group-hover:bg-primary/12 transition-colors">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-sm font-bold text-foreground">Il mio diario</h3>
                <p className="text-[11px] text-muted-foreground">Scrivi pensieri per chi verra dopo</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
            </CardContent>
          </Card>
        </motion.div>

        {/* Profiles section */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg font-bold text-foreground">I tuoi cari</h2>
          {profiles.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => nav("/profile/create")} className="text-xs text-accent hover:text-accent/80">
              <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
            </Button>
          )}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-card border-border/20 animate-pulse">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-secondary" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-secondary rounded w-24" />
                      <div className="h-3 bg-secondary rounded w-16" />
                    </div>
                  </div>
                  <div className="h-9 bg-secondary rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
            <Card className="bg-card border-border/30 border-dashed shadow-soft" data-testid="empty-profiles">
              <CardContent className="p-10 text-center">
                <div className="w-16 h-16 rounded-2xl bg-accent/8 flex items-center justify-center mx-auto mb-5">
                  <Sparkles className="w-7 h-7 text-accent/40" />
                </div>
                <h3 className="font-serif text-base font-bold text-foreground mb-2">Inizia qui</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
                  Crea il profilo di una persona cara per iniziare a conversare con la sua AI.
                </p>
                <Button variant="premium" onClick={() => nav("/profile/create")} data-testid="create-first-profile">
                  <Plus className="w-4 h-4 mr-2" /> Crea il primo profilo
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {profiles.map((profile, i) => (
              <motion.div key={profile.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Card className="bg-card border-border/30 shadow-soft hover:shadow-medium transition-all duration-300 group"
                  data-testid={`profile-card-${profile.id}`}>
                  <CardContent className="p-5 flex flex-col h-full">
                    <div className="flex items-center gap-3 mb-4">
                      {profile.photos?.length > 0 ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${profile.photos[0]}`} alt={profile.nome}
                          className="w-12 h-12 rounded-full object-cover ring-2 ring-secondary" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center ring-2 ring-secondary">
                          <span className="text-base font-bold text-accent">{profile.nome[0]}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-base font-bold text-foreground truncate">{profile.nome}</h3>
                        <p className="text-[11px] text-muted-foreground capitalize truncate">
                          {profile.relazione}{profile.soprannome ? ` · "${profile.soprannome}"` : ""}
                        </p>
                      </div>
                      {profile.voice_id && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 whitespace-nowrap">voce</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-4">
                      <Clock className="w-3 h-3" />
                      <span>Creato il {new Date(profile.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</span>
                      {profile.photos?.length > 0 && <span>· {profile.photos.length} foto</span>}
                    </div>

                    <div className="flex gap-2 mt-auto">
                      <Button variant="premium" size="default" className="flex-1 group-hover:shadow-md transition-shadow"
                        onClick={() => nav(`/chat/${profile.id}`)} data-testid={`chat-btn-${profile.id}`}>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Parla con {profile.soprannome || profile.nome}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-accent flex-shrink-0"
                        onClick={() => nav(`/profile/${profile.id}/personality`)} data-testid={`personality-btn-${profile.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
